import { z } from "zod";
import type {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import type { ConsentGovernanceVersionWrite } from "src/api/client-api/consent/useManageConsentGovernanceAdmin";
import { getConsentVersionStatus } from "../consentVersionStatus";

export const ConsentItemVersionFormSchema = z.object({
    consentItemIri: z.string().trim().min(1, "Consent item is required"),
    version: z.string().trim().min(1, "Version is required").max(50, "Version cannot exceed 50 characters"),
    title: z.string().trim().min(1, "Version title is required").max(255, "Version title cannot exceed 255 characters"),
    summary: z.string().optional(),
    content: z.string().optional(),
    documentUrl: z.union([z.literal(""), z.string().url("Document URL must be a valid URL")]).optional(),
    published: z.boolean(),
});

export type ConsentItemVersionFormData = z.infer<typeof ConsentItemVersionFormSchema>;

export type ConsentVersionLocalizedDraft = {
    content: string;
    dirty: boolean;
    documentUrl: string;
    initialized: boolean;
    summary: string;
    title: string;
};

export type ConsentItemOption = {
    "@id": IRI;
    id: number;
    name: string;
    code: string;
};

export const normalizeOptionalString = (value?: string) => {
    const normalized = value?.trim();

    return normalized && normalized.length > 0 ? normalized : null;
};

export const mapConsentItemToOption = (item: ConsentGovernanceItem): ConsentItemOption => ({
    "@id": item["@id"] as IRI,
    id: item.id,
    name: item.name,
    code: item.code,
});

export const getConsentItemIri = (value: ConsentGovernanceVersion["consentItem"] | undefined): IRI | "" => {
    if (!value) {
        return "";
    }

    if (typeof value === "string") {
        return value as IRI;
    }

    if (typeof value["@id"] === "string") {
        return value["@id"] as IRI;
    }

    return `/api/consent-items/${value.id}` as IRI;
};

export const getConsentItemId = (value: ConsentGovernanceVersion["consentItem"] | undefined): number | undefined => {
    if (!value) {
        return undefined;
    }

    if (typeof value === "string") {
        const rawId = value.split("/").filter(Boolean).at(-1);
        const parsedId = Number(rawId);

        return Number.isNaN(parsedId) ? undefined : parsedId;
    }

    return value.id;
};

export const getFallbackConsentItemOption = (
    value: ConsentGovernanceVersion["consentItem"] | undefined,
): ConsentItemOption | null => {
    if (!value) {
        return null;
    }

    if (typeof value === "string") {
        const rawId = value.split("/").filter(Boolean).at(-1) ?? value;
        const parsedId = Number(rawId);

        return Number.isNaN(parsedId) ? null : (
                {
                    "@id": value as IRI,
                    id: parsedId,
                    name: rawId,
                    code: rawId,
                }
            );
    }

    return {
        "@id": (typeof value["@id"] === "string" ? value["@id"] : `/api/consent-items/${value.id}`) as IRI,
        id: value.id,
        name: value.name ?? value.code ?? `Consent item ${value.id}`,
        code: value.code ?? String(value.id),
    };
};

export const getConsentVersionGlobalSyncKey = (
    consentVersion: ConsentGovernanceVersion | undefined,
    preselectedConsentItem: ConsentGovernanceItem | undefined,
) =>
    consentVersion ?
        [
            consentVersion.id,
            preselectedConsentItem?.["@id"] ?? getConsentItemIri(consentVersion.consentItem),
            consentVersion.version,
            getConsentVersionStatus(consentVersion),
        ].join("|")
    :   "new";

export const buildConsentVersionPayload = (
    values: ConsentItemVersionFormData,
    draft: Pick<ConsentVersionLocalizedDraft, "content" | "documentUrl" | "summary" | "title">,
): ConsentGovernanceVersionWrite => ({
    consentItem: values.consentItemIri as IRI,
    version: values.version.trim(),
    title: draft.title.trim(),
    summary: normalizeOptionalString(draft.summary),
    content: normalizeOptionalString(draft.content),
    documentUrl: normalizeOptionalString(draft.documentUrl),
    published: values.published,
});
